import { Sprout } from 'lucide-react'
import { getVerticalMeta, useVertical, type Vertical } from './useVertical'

/**
 * The OSLE brand mark: vertical tile + wordmark + tagline.
 *
 * Single source of truth for the top-left of every header. It previously lived in
 * four places — `AppShell` (signed in), `PublicHeader`, `ThriftDiscoveryFrame`, and
 * `HomestayDiscoveryFrame` — which had drifted apart: different tile sizes and
 * corner radii, different wordmark sizes, and a tagline that appeared only when
 * signed in. Signing in visibly changed the brand, which read as a different
 * product. Callers supply the surrounding `<Link>`/`<div>` and layout; this owns
 * only the mark itself.
 */
export function VerticalBrand({
  vertical,
  asHeading = false,
  neutral = false,
}: {
  /** Override the active vertical (frames that are pinned to one). */
  vertical?: Vertical
  /** Render the wordmark as `<h1>`. Use on surfaces that have no other page title. */
  asHeading?: boolean
  /** Neutral OSLE mark for cross-vertical operators, who filter rather than switch. */
  neutral?: boolean
}) {
  const { activeVertical } = useVertical()
  const meta = getVerticalMeta(vertical ?? activeVertical)

  const Icon = neutral ? Sprout : meta.icon
  const label = neutral ? 'OSLE' : `OSLE ${meta.label}`
  const tagline = neutral ? 'Operating System of Local Economy' : meta.tagline
  const Wordmark = asHeading ? 'h1' : 'span'

  return (
    <>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-brand-950 text-white">
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0">
        {/* The prefix is hidden visually, never dropped from the accessible name:
            assistive tech should still hear the full brand on a phone, and the
            name would otherwise lose the space between "OSLE" and the vertical. */}
        <Wordmark aria-label={label} className="block truncate text-lg font-bold text-stone-950">
          {neutral ? (
            'OSLE'
          ) : (
            <>
              {/* Dropped below sm: "OSLE Farm-to-Table" crowded the header row on
                  phones and pushed the right-hand icon cluster out of frame. The
                  vertical name plus the tile still identifies the product. */}
              <span className="hidden sm:inline">OSLE </span>
              {meta.label}
            </>
          )}
        </Wordmark>
        {/* Hidden on the narrowest screens only — the header row there is tight,
            and the wordmark alone still identifies the vertical. */}
        <span className="hidden truncate text-xs text-stone-500 sm:block">{tagline}</span>
      </span>
    </>
  )
}
